import { FC, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Checkbox from '@mui/material/Checkbox';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Pagination from '@mui/material/Pagination';
import Button from '@mui/material/Button';
import TableFooter from '@mui/material/TableFooter';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import { tableColumns } from '../model/constants';
import DownloadIcon from '@mui/icons-material/Download';
import IconButton from '@mui/material/IconButton';
import { getDateTime } from '../../Certificates/lib/utils';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import * as locales from '@mui/material/locale';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import {
    StarryBackground,
    NebulaEffect,
    ContentContainer,
    HeaderContainer,
    TitleContainer,
    BackButton,
    StyledTableContainer,
    StyledDownloadButton,
    StyledSelect,
    StyledPagination,
    FooterContainer,
    StyledTypography,
} from './styled';

const getDispensaryComorbidRecordsURL =
    process.env.REACT_APP_API_URL + 'api/DispensaryObservation/GetDispensaryComorbidRecords';

const getDispensaryComorbidReportURL =
    process.env.REACT_APP_API_URL + 'api/DispensaryObservation/GetDispensaryComorbidReport';

const title = 'Диспансерное наблюдение';

interface PatientData {
    fullName: string;
    birthDate: Date;
    age: number;
    gender: string;
    socialStatus: string;
    disabilityGroup: string;
    socialServices: boolean;
    moBindName: string;
    areaNumber: string;
    address: string;
    hasI48: boolean;
    hasDispensaryI48: boolean;
    hasI50: boolean;
    hasDispensaryI50: boolean;
    hasI10_I13: boolean;
    hasDispensaryI10_I13: boolean;
    hasI65_2: boolean;
    hasDispensaryI65_2: boolean;
    hasI20_I25: boolean;
    hasDispensaryI20_I25: boolean;
    hasI26: boolean;
    hasDispensaryI26: boolean;
    hasE78: boolean;
    hasDispensaryE78: boolean;
    lastPCIProcedureDate: Date | null;
    lastCABGProcedureDate: Date | null;
    lastCoronaryAngiographyDate: Date | null;
    lastBCAScanDate: Date | null;
    lastVascularSurgeonConsultDate: Date | null;
    hasHistoryI21_I22: boolean;
    hasDispensaryI21_I22: boolean;
    lastDischargeI21_I22Date: Date | null;
    hasHistoryI60_I64: boolean;
    hasDispensaryI60_I64: boolean;
    lastDischargeI60_I64Date: Date | null;
    lastDischargeI48Date: Date | null;
    lastDischargeI10_I13Date: Date | null;
    lastDischargeI65_2Date: Date | null;
    lastDischargeI20_I25Date: Date | null;
    lastDischargeI26Date: Date | null;
    hasE10_E11: boolean;
    hasDispensaryE10_E11: boolean;
    hasJ44: boolean;
    hasDispensaryJ44: boolean;
    hasN18: boolean;
    hasDispensaryN18: boolean;
    emergencyCallsCount: number;
    targetDiastolicPressure?: string;
    actualDiastolicPressure: string;
    isTargetBloodPressureAchieved: boolean;
    i48NextPlannedVisitDate: string;
    actualHeartRate: string;
    isTargetHeartRateAchieved: boolean;
    e78NextPlannedVisitDate: string;
    actualLDL: string;
    isTargetLDLAchieved: boolean;
    e10E11NextPlannedVisitDate?: string;
    actualHbA1c: string;
    isTargetHbA1cAchieved: boolean;
    chA2DS2VAScScore: number;
    isAnticoagulantTherapyPrescribed: boolean;
    pmoCompletionDate: Date | null;
    cvdMonitoringCompletionDate: Date | null;
    isStableClinicalEffectAchieved: boolean;
    phoneNumber?: string;
    i50NextPlannedVisitDate?: string;
    i10I13NextPlannedVisitDate?: string;
    targetSystolicPressure?: string;
    actualSystolicPressure?: string;
    i652NextPlannedVisitDate?: string;
    i20I25NextPlannedVisitDate?: string;
    i26NextPlannedVisitDate?: string;
    j44NextPlannedVisitDate?: string;
    n18NextPlannedVisitDate?: string;
}

const tableColumnsValue = tableColumns.map(({ value }) => value);

const getColumnWidth = (index: number, key: string) => {
    // ФИО - самый широкий столбец
    if (index === 0) return '300px';

    // Булевы значения - узкие столбцы
    if (
        key.includes('has') ||
        key.includes('achieved') ||
        key.includes('therapy') ||
        key.includes('socialservices') ||
        key.includes('stableclinicaleffect')
    )
        return '100px';

    // Возраст, баллы, номера - узкие столбцы
    if (key.includes('age') || key.includes('score') || key.includes('areanumber') || key.includes('emergencycalls'))
        return '120px';

    // Пол - очень узкий столбец
    if (key.includes('gender')) return '80px';

    // Даты - средние столбцы
    if (key.includes('date') || key.includes('Date')) return '150px';

    // АД, ЧСС, уровни - средние столбцы
    if (key.includes('pressure') || key.includes('heartrate') || key.includes('ldl') || key.includes('hba1c'))
        return '160px';

    // Адрес - широкий столбец
    if (key.includes('address')) return '250px';

    // Остальные - средние столбцы
    return '180px';
};

const DispensaryObservation: FC = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [dispensaryArray, setDispensaryArray] = useState<
        | {
              expiredCount: number;
              list: PatientData[];
              totalCount: number;
          }
        | undefined
    >(undefined);
    const [page, setPage] = useState(1);
    const [isNeedRefetch, setIsNeedRefetch] = useState(false);
    const [isDocumentLoading, setIsDocumentLoading] = useState(false);
    const [selected, setSelected] = useState<readonly number[]>([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const getDispensaryComorbidRecords = useCallback(() => {
        setIsLoading(true);

        axios
            .get(`${getDispensaryComorbidRecordsURL}?skip=${(page - 1) * rowsPerPage}&take=${rowsPerPage}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            })
            .then((data) => {
                setDispensaryArray(data.data);
                setIsLoading(false);
            })
            .catch((err) => {
                if (err.response.status === 401) {
                    localStorage.clear();
                    navigate('/login');
                } else {
                    alert('Не удалось загрузить диспансерное наблюдение, ' + err);
                }
                setIsLoading(false);
            });
    }, [page, navigate, rowsPerPage]);

    useEffect(() => {
        document.title = title;

        getDispensaryComorbidRecords();
    }, []);

    useEffect(() => {
        if (isNeedRefetch) {
            try {
                getDispensaryComorbidRecords();
            } catch (err) {
                console.log(err);
            }

            setIsNeedRefetch(false);
        }
    }, [isNeedRefetch, getDispensaryComorbidRecords]);

    const handleChangePage = (newPage: number) => {
        setPage(newPage);
        setIsNeedRefetch(true);
    };

    const handleChangeRowsPerPage = (value: string) => {
        setRowsPerPage(parseInt(value, 10));
        setIsNeedRefetch(true);
        setPage(1);
    };

    const handleDownloadXlsx = () => {
        setIsDocumentLoading(true);

        try {
            axios
                .get(getDispensaryComorbidReportURL, {
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                })
                .then(({ headers, data }) => {
                    const disposition = headers['content-disposition'];

                    const blob = new Blob([data], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    });

                    let fileName = 'file.xlsx';

                    if (disposition && disposition.indexOf('filename=') !== -1) {
                        const matches = disposition.match(/filename="?([^"]+)"?/);
                        if (matches != null && matches[1]) {
                            fileName = matches[1];
                        }
                    }

                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = fileName;
                    link.click();
                    window.URL.revokeObjectURL(link.href); // очистка

                    setIsDocumentLoading(false);
                })
                .catch((err) => {
                    console.log(err);

                    setIsDocumentLoading(false);
                });
        } catch (err) {
            console.log(err);
        }
    };

    const handlePatientClick = (event: React.MouseEvent<unknown>, id: number) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected: readonly number[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        setSelected(newSelected);
    };

    const theme = useTheme();

    const themeWithLocale = createTheme(theme, locales['ruRU']);

    return isLoading ? (
        <StarryBackground>
            <NebulaEffect />
            <Box
                display='flex'
                justifyContent='center'
                alignItems='center'
                height='100vh'
                position='relative'
                zIndex={10}
            >
                <CircularProgress sx={{ color: 'white' }} />
            </Box>
        </StarryBackground>
    ) : (
        <StarryBackground>
            <NebulaEffect />
            <ThemeProvider theme={themeWithLocale}>
                <ContentContainer>
                    <HeaderContainer>
                        <BackButton onClick={() => navigate('/')} startIcon={<ArrowBackIcon />}>
                            Назад
                        </BackButton>
                        <TitleContainer>
                            <Typography
                                color='white'
                                variant='h1'
                                sx={{
                                    fontSize: '48px',
                                    fontWeight: 'bold',
                                    textShadow: `
                                        0 0 10px rgba(59, 130, 246, 0.5),
                                        0 0 20px rgba(59, 130, 246, 0.3),
                                        0 0 30px rgba(59, 130, 246, 0.2)
                                    `,
                                    animation: 'titleGlow 3s ease-in-out infinite',
                                    '@keyframes titleGlow': {
                                        '0%, 100%': {
                                            textShadow: `
                                                0 0 10px rgba(59, 130, 246, 0.5),
                                                0 0 20px rgba(59, 130, 246, 0.3),
                                                0 0 30px rgba(59, 130, 246, 0.2)
                                            `,
                                        },
                                        '50%': {
                                            textShadow: `
                                                0 0 15px rgba(59, 130, 246, 0.8),
                                                0 0 25px rgba(59, 130, 246, 0.5),
                                                0 0 35px rgba(59, 130, 246, 0.3)
                                            `,
                                        },
                                    },
                                }}
                            >
                                К
                                <IconButton
                                    href='https://rmiac18.ru/'
                                    target='_blank'
                                    sx={{
                                        padding: 0,
                                        marginBottom: '10px',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            transform: 'scale(1.1)',
                                            cursor: 'pointer',
                                        },
                                    }}
                                >
                                    <img src='/logo-cosmos.svg' alt='custom icon' style={{ width: 50, height: 50 }} />
                                </IconButton>
                                СМОС
                            </Typography>
                        </TitleContainer>
                        <Typography
                            color='white'
                            variant='h3'
                            sx={{
                                fontSize: '24px',
                                fontWeight: 'normal',
                                textAlign: 'center',
                                marginTop: '10px',
                                textShadow: `
                                        0 0 5px rgba(59, 130, 246, 0.3),
                                        0 0 10px rgba(59, 130, 246, 0.2)
                                    `,
                            }}
                        >
                            Регистр коморбидных
                        </Typography>
                        <Box width='100px' /> {/* Spacer для балансировки */}
                    </HeaderContainer>

                    <StyledTableContainer sx={{ maxHeight: 'calc(100vh - 200px)', flex: 1 }}>
                        <Table aria-label='sticky table'>
                            <TableHead>
                                <TableRow>
                                    {tableColumns.map((column, index) => {
                                        return (
                                            <TableCell
                                                sx={{
                                                    minWidth: getColumnWidth(index, column.value),
                                                }}
                                                key={column.value.toLowerCase()}
                                            >
                                                {index + 1}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                                <TableRow
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 100,
                                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                    }}
                                >
                                    {tableColumns.map(({ value, label }, index) => (
                                        <TableCell
                                            sx={{
                                                minWidth: getColumnWidth(index, value),
                                                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                            }}
                                            key={value.toLowerCase()}
                                        >
                                            {label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dispensaryArray?.list.map((row, rowIndex) => {
                                    const isItemSelected = selected.includes(rowIndex);

                                    const newRow: any = {};

                                    Object.keys(row).map((key, index) => {
                                        const lowKey = key.toLowerCase();
                                        if (tableColumns.some((column) => column.value.toLowerCase() === lowKey)) {
                                            // @ts-ignore
                                            newRow[lowKey] = row[key];
                                        }
                                    });

                                    return (
                                        <TableRow
                                            hover
                                            role='checkbox'
                                            tabIndex={-1}
                                            key={rowIndex}
                                            onClick={(event) => handlePatientClick(event, rowIndex)}
                                            aria-checked={isItemSelected}
                                            selected={isItemSelected}
                                        >
                                            {tableColumnsValue.map((elem, index) => {
                                                const value = newRow[elem.toLowerCase()];

                                                const useThisValue: any = value === null ? '' : value;
                                                const isValueDate = elem.toLowerCase().includes('date');
                                                const isFirstElem = elem.toLowerCase() === 'fullname';

                                                const dateValue =
                                                    getDateTime(new Date(useThisValue), true) === 'NaN.NaN.NaN'
                                                        ? useThisValue
                                                        : getDateTime(new Date(useThisValue), true);

                                                return (
                                                    <TableCell
                                                        key={elem}
                                                        sx={{
                                                            minWidth: getColumnWidth(index, elem),
                                                            verticalAlign: 'middle',
                                                            position: isFirstElem ? 'sticky' : 'static',
                                                            left: isFirstElem ? 0 : 'auto',
                                                            zIndex: isFirstElem ? 5 : 'auto',
                                                            backgroundColor: isFirstElem
                                                                ? 'rgba(0, 0, 0, 0.8)'
                                                                : 'transparent',
                                                            backdropFilter: isFirstElem ? 'blur(10px)' : 'none',
                                                        }}
                                                    >
                                                        {isFirstElem && (
                                                            <>
                                                                {rowIndex + 1 + rowsPerPage * (page - 1)}
                                                                <Checkbox
                                                                    sx={{ ml: 1 }}
                                                                    color='primary'
                                                                    checked={isItemSelected}
                                                                />
                                                            </>
                                                        )}
                                                        {isValueDate ? dateValue : useThisValue}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>

                    <FooterContainer>
                        <StyledDownloadButton
                            onClick={handleDownloadXlsx}
                            startIcon={
                                isDocumentLoading ? (
                                    <CircularProgress size={20} sx={{ color: 'white' }} />
                                ) : (
                                    <DownloadIcon />
                                )
                            }
                            disabled={isDocumentLoading}
                            sx={{ width: 190 }}
                        >
                            {isDocumentLoading ? 'Загружаю xlsx' : 'Загрузить xlsx'}
                        </StyledDownloadButton>

                        <StyledTypography>
                            Общее количество записей: {dispensaryArray?.totalCount || 0}
                        </StyledTypography>

                        <Box display='flex' flexDirection='row' alignItems='center' gap={1}>
                            <StyledTypography>Строк на странице:</StyledTypography>
                            <StyledSelect
                                size='small'
                                variant='outlined'
                                value={rowsPerPage}
                                onChange={(e) => handleChangeRowsPerPage((e.target as HTMLSelectElement).value)}
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                            </StyledSelect>
                        </Box>

                        <StyledPagination>
                            <Pagination
                                count={Math.ceil((dispensaryArray?.totalCount || 0) / rowsPerPage)}
                                page={page}
                                onChange={(event, value) => handleChangePage(value)}
                                color='primary'
                                size='large'
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        color: 'white',
                                        border: '1px solid rgba(59, 130, 246, 0.3)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                            borderColor: 'rgba(6, 182, 212, 0.5)',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(59, 130, 246, 0.4)',
                                            borderColor: 'rgba(6, 182, 212, 0.8)',
                                        },
                                    },
                                }}
                            />
                        </StyledPagination>
                    </FooterContainer>
                </ContentContainer>
            </ThemeProvider>
        </StarryBackground>
    );
};

export default DispensaryObservation;
